import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useGuides } from '@/features/guides/hooks/useGuides';
import { GuideSearchBar, type SearchValues } from '@/features/guides/components/GuideSearchBar';
import { GuideListingCard } from '@/features/guides/components/GuideListingCard';
import { getGuideBookPath } from '@/features/guides/utils/guideBookPath';
import { GuidesPagination } from '@/features/guides/components/GuidesPagination';
import {
  buildGuidesSearchParams,
  parseGuidesSearchParams,
  type GuidesSearchParams,
} from '@/features/guides/utils/searchParams';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AuthModal } from '@/features/auth/components/AuthModal';
import { BookingCheckoutSheet } from '@/features/booking/components/BookingCheckoutSheet';

function GuideCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Skeleton variant="rectangular" sx={{ aspectRatio: '4/3' }} />
      <Box sx={{ p: 2.5 }}>
        <Skeleton width="65%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton width="45%" height={16} sx={{ mb: 1.5 }} />
        <Skeleton width="80%" height={16} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
          <Skeleton variant="rounded" width={72} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Skeleton width={80} height={32} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={60} height={36} />
            <Skeleton variant="rounded" width={90} height={36} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const TRUST_ITEMS = [
  {
    icon: VerifiedUserOutlinedIcon,
    title: '100% Verified Guides',
    desc: 'All guides are background verified',
  },
  {
    icon: LocalOfferOutlinedIcon,
    title: 'Best Price Guarantee',
    desc: 'Get the best rates available',
  },
  {
    icon: LockOutlinedIcon,
    title: 'Secure Booking',
    desc: 'Your payment is protected',
  },
  {
    icon: SupportAgentOutlinedIcon,
    title: '24/7 Support',
    desc: "We're here to help you",
  },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Recommended' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'price', label: 'Price: Low to High' },
  { value: 'reviewCount', label: 'Most Reviewed' },
];

const FILTER_CHIPS = [
  { label: 'All Filters', icon: TuneOutlinedIcon },
  { label: 'Verified Only', active: true },
  { label: 'Rating 4.5+' },
  { label: 'Available Today' },
];

export function GuidesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [checkoutGuideId, setCheckoutGuideId] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingBookGuideId, setPendingBookGuideId] = useState<string | null>(null);

  const filters = useMemo(
    () => parseGuidesSearchParams(searchParams.toString()),
    [searchParams]
  );

  const searchValues = useMemo<SearchValues>(
    () => ({ city: filters.city, date: filters.travelDate, language: filters.language }),
    [filters.city, filters.travelDate, filters.language]
  );

  const { data, isLoading, isError, error } = useGuides(filters);

  const handleSearch = (values: SearchValues) => {
    const newFilters: GuidesSearchParams = {
      page: 1,
      limit: 12,
      city: values.city,
      travelDate: values.date,
      language: values.language,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    };
    setSearchParams(buildGuidesSearchParams(newFilters));
  };

  const handleSortChange = (e: SelectChangeEvent) => {
    const val = e.target.value;
    setSearchParams(
      buildGuidesSearchParams({
        ...filters,
        page: 1,
        sortBy: val,
        sortOrder: val === 'price' ? 'asc' : 'desc',
      })
    );
  };

  const handlePageChange = (page: number) => {
    setSearchParams(buildGuidesSearchParams({ ...filters, page }));
  };

  const handleBookNow = (guideId: string) => {
    if (!isAuthenticated) {
      setPendingBookGuideId(guideId);
      setAuthModalOpen(true);
      return;
    }
    setCheckoutGuideId(guideId);
  };

  const handleAuthSuccess = (route: string) => {
    setAuthModalOpen(false);
    if (route.startsWith('/dashboard') || route.startsWith('/admin')) {
      navigate(route, { replace: true });
      setPendingBookGuideId(null);
      return;
    }
    if (pendingBookGuideId) {
      const path = getGuideBookPath(
        pendingBookGuideId,
        filters.travelDate,
        filters.city,
        filters.travelers
      );
      navigate(path);
      setPendingBookGuideId(null);
    }
  };

  const guides = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: { xs: 64, md: 72 },
          zIndex: 100,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <GuideSearchBar variant="page" initialValues={searchValues} onSearch={handleSearch} />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '1.375rem', md: '1.5rem' },
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}
            >
              {isLoading ? (
                <Skeleton width={220} />
              ) : (
                <>
                  {pagination?.total ?? guides.length} Local Guides
                  {filters.city && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                      }}
                    >
                      {' '}in {filters.city}
                    </Typography>
                  )}
                </>
              )}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: 'text.secondary',
                mt: 0.25,
              }}
            >
              Based on your search criteria
            </Typography>
          </Box>

          <FormControl size="small">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                Sort by:
              </Typography>
              <Select
                value={filters.sortBy ?? 'createdAt'}
                onChange={handleSortChange}
                variant="outlined"
                sx={{
                  minWidth: 160,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& .MuiSelect-select': { py: 1.25 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
                    },
                  },
                }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 4,
          }}
        >
          {FILTER_CHIPS.map((chip) => (
            <Chip
              key={chip.label}
              label={chip.label}
              icon={chip.icon ? <chip.icon sx={{ fontSize: '18px !important' }} /> : undefined}
              variant={chip.active ? 'filled' : 'outlined'}
              onClick={() => {}}
              sx={{
                borderRadius: 2,
                fontSize: '0.8125rem',
                fontWeight: 500,
                height: 36,
                px: 0.5,
                ...(chip.active
                  ? {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }
                  : {
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50',
                      },
                    }),
              }}
            />
          ))}
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {(error as { response?: { status?: number } })?.response?.status === 429
              ? 'Too many requests — please wait a moment and refresh.'
              : ((error as Error)?.message ?? 'Failed to load guides. Please try again.')}
          </Alert>
        )}

        {isLoading ? (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
                <GuideCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : guides.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: { xs: 8, md: 12 },
              px: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'text.primary',
                mb: 1,
              }}
            >
              No guides found
            </Typography>
            <Typography
              sx={{
                fontSize: '0.9375rem',
                color: 'text.secondary',
              }}
            >
              Try adjusting your search criteria or exploring a different destination.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {guides.map((guide) => (
                <Grid key={guide._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <GuideListingCard
                    guide={guide}
                    onBookNow={handleBookNow}
                    travelDate={filters.travelDate}
                    city={filters.city}
                    travelers={filters.travelers}
                  />
                </Grid>
              ))}
            </Grid>

            <GuidesPagination
              page={pagination?.page ?? 1}
              totalPages={pagination?.totalPages ?? 1}
              onChange={handlePageChange}
            />
          </>
        )}

        <Box
          sx={{
            mt: 6,
            mb: 2,
            p: { xs: 3, md: 4 },
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {TRUST_ITEMS.map((item) => (
              <Grid key={item.title} size={{ xs: 6, md: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      bgcolor: 'primary.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <item.icon sx={{ fontSize: 22, color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                        mb: 0.25,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        color: 'text.secondary',
                        lineHeight: 1.4,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <AuthModal
        open={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
          setPendingBookGuideId(null);
        }}
        onAuthSuccess={handleAuthSuccess}
        title="Sign in to book"
        subtitle="Create a free account or sign in to book a guide for your trip."
      />

      {checkoutGuideId && (
        <BookingCheckoutSheet
          open
          onClose={() => setCheckoutGuideId(null)}
          guideId={checkoutGuideId}
          travelDate={filters.travelDate}
          city={filters.city}
        />
      )}
    </Box>
  );
}
